
import React from "react";
import { i18nManager } from "../../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, HostRole, HostService, IHostManager, IHostManagerEarning, IMember, ITournamentEdition, ITournamentEditionAnalytics, ITournamentTransaction, SanitizedResponse, TournamentService } from "../../../../../../services";
import { Dropdown, FormControl, DateTimeInput, DateTimePickerLayout, DateHelper, DateTimePickerSelectionMode, ChartType, ChartData, Classname } from "@rivtn/noseur";
import { Utils, Schemes } from "../../../../../../utils";
import AnalCard from "../../../../../shared/analytics/AnalCard";
import { ChartManager } from "../../../../../shared/ChartManager";
import RecentTransactions from "../shared/RecentTransactions";
import { Elements } from "../../../../../shared/Elements";
import MatchFixtures from "../MatchFixtures";

interface TournamentMatchesAnalyticsProps {
    tournamentEdition: ITournamentEdition;
}

function TournamentMatchesAnalytics(props: TournamentMatchesAnalyticsProps) {

    const { tournamentEdition } = props;
    const hostService = HostService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.host.manage_tournament.analytics;
    const tournamentAnalyticsChartDataState = React.useState<ChartData>();
    const [daysDifference, setDaysDifference] = React.useState<number>(0);
    const [tournamentAnalytics, setTournamentAnalytics] = React.useState<ITournamentEditionAnalytics>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const currencySign = i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN");

    React.useEffect(fetchInformation, []);

    React.useEffect(() => {
        setDaysDifference(DateHelper.getDaysBetweenDates(
            (searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()),
            (searchParams.has("to") ? new Date(searchParams.get("to")!) : new Date())));
    }, [searchParams]);

    return (<div className="earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", alignItems: "center", marginTop: 15 }}>
                <i className="fa fa-arrow-left" style={{ cursor: "pointer" }} onClick={() => window.history.back()} />
                <span className="lined-limit" style={{ fontSize: 16, marginLeft: 10 }}>{labels.matches_anals}</span>
            </div>
            <div className="queries">
                <span className="link lined-limit" style={{ marginTop: 15, marginRight: 15 }}>{labels.export}</span>
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={i18nManager.Labels.teams.analytics.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformation);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="container" style={{ marginTop: 10 }}>
            <div className="primary" style={{ marginLeft: 0 }}>
                <div className="anal-cards" style={{ marginTop: 15 }}>
                    <AnalCard anal={tournamentAnalytics?.matches?.total} desc={labels.matches_played} title={labels.total}
                        icon="fa fa-soccer-ball" analKey="count" daysDifference={daysDifference} cardColor={"#EBF4FE"} />
                    <AnalCard anal={tournamentAnalytics?.matches?.ended} desc={labels.matches_compl} title={labels.ended}
                        icon="fa fa-soccer-ball" analKey="count" daysDifference={daysDifference} cardColor={"#EBF4FE"} />
                    <AnalCard anal={tournamentAnalytics?.matches?.upcoming} desc={labels.upcoming_matches} title={labels.upcoming}
                        icon="fa fa-soccer-ball" analKey="count" daysDifference={daysDifference} cardColor={"#EBF4FE"} />
                    <AnalCard anal={tournamentAnalytics?.matches?.ongoing} desc={labels.ongoing_matches} title={labels.ongoing}
                        icon="fa fa-soccer-ball" analKey="count" daysDifference={daysDifference} cardColor={"#EBF4FE"} />
                </div>
                {<ChartManager type={ChartType.BAR} title={labels.tournament_anal} chartDataState={tournamentAnalyticsChartDataState} />}
                <div style={{ display: "flex", margin: "10px 0px 20px 0px", justifyContent: "space-between" }}>
                    <span className="l">{labels.recent_match_fixtures}</span>
                    <ReactRouterDom.NavLink to={`/hosts/${tournamentEdition.tournament.host.public_id}/tournament/${tournamentEdition.tournament.external_id}/analytics/matches/all`}>{labels.view_all_match_fixs}</ReactRouterDom.NavLink>
                </div>
                <MatchFixtures tournamentEdition={tournamentEdition} showRecent={true} />
            </div>
        </div>
    </div>);

    function fetchInformation() {
        const params = {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "participants.total", "matches.total", "prize_pool.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        };
        setTournamentAnalytics(undefined);
        tournamentService.getTournamentAnalytics(tournamentEdition.external_id, params).then(({ sanitized }: SanitizedResponse<ITournamentEditionAnalytics>) => {
            setTournamentAnalytics(sanitized);
        }).catch(BaseService.reportError);

        tournamentAnalyticsChartDataState[1](undefined);
        tournamentService.queryAnalyticsChartData(tournamentEdition.external_id, params).then(({ apiResponse }: SanitizedResponse<any>) => {
            tournamentAnalyticsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default TournamentMatchesAnalytics;
